import { type FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { useAppStore } from '@/stores/app-store';
import type { DataFreshness } from '@/types';

// ---------------------------------------------------------------------------
// Bottom status bar  (28 px) — full-mono telemetry strip
// ---------------------------------------------------------------------------

// 내부 소스 키 → 텔레메트리 라벨 축약
const SOURCE_LABELS: Record<string, string> = {
  earthquakes: 'USGS',
  news: 'RSS',
  'gdelt-events': 'GDELT',
  wildfires: 'FIRMS',
  market: 'MKT',
  aircraft: 'ADSB',
  acled: 'ACLED',
};

const STATUS_SEG: Record<DataFreshness['status'], { text: string; cls: string }> = {
  fresh: { text: 'OK', cls: 'text-emerald-400' },
  stale: { text: '--', cls: 'text-slate-600' },
  loading: { text: '--', cls: 'text-slate-600 animate-pulse' },
  error: { text: 'ERR', cls: 'text-red-400' },
};

function formatUtc(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:${p(d.getUTCSeconds())}Z`;
}

export const StatusBar: FC = () => {
  const { t } = useTranslation();
  const freshness = useAppStore((s) => s.freshness);

  const entries = useMemo(() => Array.from(freshness.values()), [freshness]);

  const lastRefresh = useMemo(() => {
    if (entries.length === 0) return null;
    const latest = Math.max(...entries.map((e) => e.lastFetch));
    return new Date(latest);
  }, [entries]);

  // 라이브 UTC 시계 — 1초 틱
  const [utc, setUtc] = useState(() => formatUtc(new Date()));
  useEffect(() => {
    const id = setInterval(() => setUtc(formatUtc(new Date())), 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <footer className="h-7 flex items-center justify-between gap-3 px-4 bg-surface-panel/70 backdrop-blur-sm border-t border-surface-border font-mono tabular-nums tracking-tight text-[10px] text-slate-500 z-40 shrink-0 select-none">
      {/* ---- Left: bracketed data-source telemetry ---- */}
      <div className="flex items-center gap-2 overflow-hidden">
        {entries.map((e) => {
          const seg = STATUS_SEG[e.status];
          return (
            <span
              key={e.source}
              className={clsx('whitespace-nowrap', seg.cls)}
              title={`${e.source}: ${e.status}`}
            >
              [{SOURCE_LABELS[e.source] ?? e.source.toUpperCase()}:{seg.text}]
            </span>
          );
        })}
      </div>

      {/* ---- Center: last refresh ---- */}
      <div className="hidden md:block whitespace-nowrap">
        {lastRefresh
          ? t('time.lastUpdated', { time: lastRefresh.toLocaleTimeString() })
          : '—'}
      </div>

      {/* ---- Right: branding + live UTC clock ---- */}
      <div className="flex items-center gap-3 shrink-0">
        <span className="tracking-wider uppercase text-slate-600 hidden sm:inline">Quasar Engine V1</span>
        <span className="text-neon-cyan">{utc}</span>
      </div>
    </footer>
  );
};
